import React, { useState, useMemo } from 'react';
import { VStack, HStack, Text, Box, Input, Button, Badge } from '@chakra-ui/react';
import { Users, Search, UserCheck } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useGetAssistantDoctorsQuery, useUpdateAssistantMutation, UserDoctorResource, useGetUserByIdQuery } from '../api';
import { useGetOrganizationDoctorsQuery } from '@/entities/organizations/api';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ErrorAlert from '@/components/ErrorAlert';

interface ManageDoctorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const ManageDoctorsModal: React.FC<ManageDoctorsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoctorEmails, setSelectedDoctorEmails] = useState<string[]>([]);
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });

  // Get the assistant's user ID from the profile
  const assistantUserId = user?.id;
  const organizationId = user?.organization?.id;

  // Update assistant mutation
  const [updateAssistant, { isLoading: isUpdating }] = useUpdateAssistantMutation();

  // Fetch doctors assigned to this assistant
  const {
    data: assistantDoctorsData,
    isLoading: isLoadingAssistantDoctors,
    error: assistantDoctorsError,
  } = useGetAssistantDoctorsQuery(assistantUserId!, {
    skip: !assistantUserId,
  });

  // Fetch all doctors in the organization
  const {
    data: organizationDoctorsData,
    isLoading: isLoadingOrgDoctors,
    error: orgDoctorsError,
  } = useGetOrganizationDoctorsQuery(organizationId!, {
    skip: !organizationId,
  });

  // Extract doctors from assistant connections
  const assignedDoctors = useMemo(() => {
    return assistantDoctorsData?.map(connection => connection.doctor) || [];
  }, [assistantDoctorsData]);

  // Initialize selected emails when modal opens and data loads
  React.useEffect(() => {
    if (isOpen && assistantDoctorsData) {
      const emails = assistantDoctorsData.map(connection => connection.doctor.email);
      setSelectedDoctorEmails(emails);
    }
  }, [isOpen, assistantDoctorsData]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedDoctorEmails([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Get assigned doctor IDs for filtering
  const assignedDoctorIds = useMemo(() => {
    return new Set(assignedDoctors.map(doctor => doctor.id));
  }, [assignedDoctors]);

  // All organization doctors
  const allDoctors = organizationDoctorsData || [];

  // Combine and deduplicate doctors, marking their selection status
  const doctorsWithStatus = useMemo(() => {
    const doctorMap = new Map<number, UserDoctorResource & { isSelected: boolean }>();
    
    // Add all organization doctors
    allDoctors.forEach(doctor => {
      doctorMap.set(doctor.id, {
        ...doctor,
        isSelected: selectedDoctorEmails.includes(doctor.email),
      });
    });

    return Array.from(doctorMap.values());
  }, [allDoctors, selectedDoctorEmails]);

  // Filter doctors based on search term
  const filteredDoctors = useMemo(() => {
    if (!searchTerm) return doctorsWithStatus;
    
    const search = searchTerm.toLowerCase();
    return doctorsWithStatus.filter(doctor => 
      doctor.name?.toLowerCase().includes(search) ||
      doctor.email?.toLowerCase().includes(search)
    );
  }, [doctorsWithStatus, searchTerm]);

  const isLoading = isLoadingAssistantDoctors || isLoadingOrgDoctors;

  const handleDoctorToggle = (doctor: UserDoctorResource) => {
    setSelectedDoctorEmails(prev => {
      if (prev.includes(doctor.email)) {
        return prev.filter(email => email !== doctor.email);
      } else {
        return [...prev, doctor.email];
      }
    });
  };

  const handleSaveAssignments = async () => {
    if (!user) return;

    try {
      await updateAssistant({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        doctor_emails: selectedDoctorEmails,
      }).unwrap();

      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to update assignments:', err);
      // Could show error message to user
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setSelectedDoctorEmails([]);
    setSearchTerm('');
    onClose();
  };



  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          <HStack>
            <Users size={20} />
            <Text>Manage Doctors</Text>
          </HStack>
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={6} align="stretch">
          {/* Assistant Info */}
          <Box 
            p={4} 
            borderWidth={1} 
            borderRadius="md" 
            borderColor="gray.200" 
            bg="gray.50"
            _dark={{ 
              borderColor: "gray.600", 
              bg: "gray.800" 
            }}
          >
            <VStack align="start" gap={2}>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" _dark={{ color: "gray.400" }}>
                Managing as:
              </Text>
              <Text fontWeight="semibold">{user?.first_name} {user?.last_name}</Text>
              <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                {user?.email} • Assistant
              </Text>
              {user?.organization && (
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                  Organization: {user.organization.name}
                </Text>
              )}
              <HStack gap={4} mt={2}>
                <Text fontSize="sm" color="blue.600" _dark={{ color: "blue.400" }}>
                  Selected: {selectedDoctorEmails.length}
                </Text>
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                  Total: {allDoctors.length}
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Search */}
          <Box>
            <HStack mb={3}>
              <Search size={16} />
              <Text fontSize="sm" fontWeight="medium">Search Doctors</Text>
            </HStack>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
            />
          </Box>

          {/* Error States */}
          {(assistantDoctorsError || orgDoctorsError) && (
            <ErrorAlert
              isError={true}
              title="Failed to load doctors"
              message="Unable to retrieve doctor information. Please try again."
            />
          )}

          {/* Loading State */}
          {isLoading && (
            <LoadingSkeleton rows={3} />
          )}

          {/* Doctors List */}
          {!isLoading && !assistantDoctorsError && !orgDoctorsError && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.600" _dark={{ color: "gray.400" }}>
                Doctors ({filteredDoctors.length})
              </Text>
              
              <VStack gap={3} align="stretch">
                {filteredDoctors.length === 0 ? (
                  <Box 
                    p={4} 
                    textAlign="center" 
                    borderWidth={1} 
                    borderRadius="md" 
                    borderStyle="dashed"
                    borderColor="gray.300"
                    _dark={{ borderColor: "gray.600" }}
                  >
                    <Text color="gray.500" _dark={{ color: "gray.400" }}>
                      {searchTerm ? 'No doctors found matching your search.' : 'No doctors in this organization.'}
                    </Text>
                  </Box>
                ) : (
                  filteredDoctors.map((doctor) => (
                    <HStack
                      key={doctor.id}
                      p={3}
                      borderWidth={1}
                      borderRadius="md"
                      borderColor={doctor.isSelected ? "blue.200" : "gray.200"}
                      bg={doctor.isSelected ? "blue.50" : "white"}
                      _dark={{ 
                        borderColor: doctor.isSelected ? "blue.600" : "gray.600", 
                        bg: doctor.isSelected ? "blue.900" : "gray.700" 
                      }}
                      cursor="pointer"
                      _hover={{
                        borderColor: doctor.isSelected ? "blue.300" : "gray.300",
                        _dark: {
                          borderColor: doctor.isSelected ? "blue.500" : "gray.500"
                        }
                      }}
                      justify="space-between"
                      align="center"
                      onClick={() => handleDoctorToggle(doctor)}
                    >
                      <HStack gap={3} flex={1}>
                        <Box
                          w={4}
                          h={4}
                          borderWidth={2}
                          borderRadius="sm"
                          borderColor={doctor.isSelected ? "blue.500" : "gray.300"}
                          bg={doctor.isSelected ? "blue.500" : "transparent"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          _dark={{
                            borderColor: doctor.isSelected ? "blue.400" : "gray.500",
                            bg: doctor.isSelected ? "blue.400" : "transparent"
                          }}
                        >
                          {doctor.isSelected && (
                            <UserCheck size={10} color="white" />
                          )}
                        </Box>
                        
                        <VStack align="start" gap={0} flex={1}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {doctor.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
                            {doctor.email}
                          </Text>
                        </VStack>
                        
                        <Badge 
                          colorPalette={doctor.isSelected ? 'blue' : 'gray'}
                          size="sm"
                        >
                          {doctor.isSelected ? 'Selected' : 'Available'}
                        </Badge>
                      </HStack>
                    </HStack>
                  ))
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAssignments}
            loading={isUpdating}
            disabled={isUpdating}
            colorPalette="blue"
          >
            Save Assignments
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ManageDoctorsModal, {
  action: 'manage-doctors',
});
